import {
  GetDashboardRequiredAlertsListParams,
  GetDashboardRequiredAlertsListResponse,
} from '@nxsys/store/slices/dashboard/types';
import {
  ApproveExpenseParams,
  ApproveExpenseResponse,
  ApprovePayrollParams,
  ApprovePayrollResponse,
  ConfirmPaymentParams,
  ConfirmPaymentResponse,
  GeneratePaymentFileParams,
  GeneratePaymentFileResponse,
  GetAgencyStatisticsParams,
  GetAgencyStatisticsResponse,
  GetAgencyUploadedFileListParams,
  GetAgencyUploadedFileListResponse,
  GetCompanyPaymentGroupingParams,
  GetCompanyPaymentGroupingResponse,
  GetGroupingPayrollByPeriodResponse,
  GetListApproverParams,
  GetListApproverResponse,
  GetStatisticsClientApprovalRejectResponse,
  GetStatisticsCommunicationResponse,
  GetStatisticsEmployeeDashboardParams,
  GetStatisticsEmployeeDashboardResponse,
  GetStatisticsEmployeeIrelandResponse,
  GetStatisticsComplianceResponse,
  GetStatisticsComplianceParams,
  SafeRecErrorListResponse,
  VeriPayeErrorListResponse,
  ComplianceErrorListParams,
  SafeRecResendParams,
  VeriPayeResendParams,
  GetStatisticsEmployeeSubcontractorResponse,
  GetStatisticsInvoicingResponse,
  GetStatisticsMiscellaneousResponse,
  GetStatisticsOnHoldResponse,
  GetStatisticsPaymentsResponse,
  GetStatisticsPayrollImportResponse,
  GetStatisticsPensionContributionParams,
  GetStatisticsPensionContributionResponse,
  GetStatisticsPensionSubcontractorsResponse,
  GetPensionContributionResponse,
  GetStatisticsRevenueSubmissionResponse,
  GetStatisticsRTISubmissionResponse,
  GetStatisticsOthersResponse,
  GetStatisticsTimesheetsResponse,
  MarkAsSubmittedCIS300Params,
  MarkAsSubmittedCIS300Response,
  RejectExpenseParams,
  RejectExpenseResponse,
  RejectPayrollParams,
  RejectPayrollResponse,
  RequestApproverParams,
  RequestApproverResponse,
  SubmitCIS300Params,
  SubmitCIS300Response,
  SubmitCISDeductionsSufferedParams,
  SubmitCISDeductionsSufferedResponse,
  SubmitEPSParams,
  SubmitEPSResponse,
  SubmitFPSParams,
  SubmitFPSResponse,
  SubmitLeaverFPSParams,
  SubmitLeaverFPSResponse,
  UploadAgencyFileParams,
  UploadAgencyFileResponse,
} from '@nxsys/types/api/dashboard';
import {
  PayBreakdownParams,
  PayBreakdownResponse,
  PayrollPeriodsResponse,
} from '@nxsys/types/api/payBreakdown';
import { UpcomingPaymentResponse } from '@nxsys/types/api/upcomingPayment';
import { TimesheetBreakdownResponse } from '@nxsys/types/api/timesheetBreakdown';
import axios from '../axios';
import { endPoints } from '../endpoints';

export const submitCIS300Api = async (
  params: SubmitCIS300Params,
): Promise<SubmitCIS300Response> => {
  const response = await axios.post(
    `${endPoints.dashboard.cisSubmitted}`,
    params,
  );
  return response.data;
};

export const markAsSubmittedCIS300Api = async (
  params: MarkAsSubmittedCIS300Params,
): Promise<MarkAsSubmittedCIS300Response> => {
  const response = await axios.post(
    `${endPoints.dashboard.maskAsSubmitted}`,
    params,
  );
  return response.data;
};

export const submitCISDeductionsSufferedApi = async (
  companyId: string,
  params: SubmitCISDeductionsSufferedParams,
): Promise<SubmitCISDeductionsSufferedResponse> => {
  const response = await axios.post(
    `${endPoints.dashboard.submitCISDeductionsSuffered(companyId)}`,
    {},
    {
      params,
    },
  );
  return response.data;
};

export const submitEPSApi = async (
  params: SubmitEPSParams,
): Promise<SubmitEPSResponse> => {
  const response = await axios.post(
    `${endPoints.dashboard.epsSubmitted}`,
    params,
  );
  return response.data;
};

export const submitFPSApi = async (
  params: SubmitFPSParams,
): Promise<SubmitFPSResponse> => {
  const response = await axios.post(
    `${endPoints.dashboard.fpsSubmitted}`,
    params,
  );
  return response.data;
};

export const submitLeaverFPSApi = async (
  params: SubmitLeaverFPSParams,
): Promise<SubmitLeaverFPSResponse> => {
  const response = await axios.post(`${endPoints.rti.leaverFPS}`, params);
  return response.data;
};

export const getStatisticsTimesheetsApi = async (
  params?: { paymentModels?: string[] },
): Promise<GetStatisticsTimesheetsResponse> => {
  const response = await axios.get(
    `${endPoints.dashboard.statistics.timesheets}`,
    { params },
  );
  return response.data;
};

export const approveExpenseApi = async (
  params: ApproveExpenseParams,
): Promise<ApproveExpenseResponse> => {
  const response = await axios.put(`${endPoints.expenses.approve}`, params);
  return response.data;
};

export const rejectExpenseApi = async (
  params: RejectExpenseParams,
): Promise<RejectExpenseResponse> => {
  const response = await axios.put(`${endPoints.expenses.reject}`, params);
  return response.data;
};

export const getStatisticsInvoicingApi =
  async (): Promise<GetStatisticsInvoicingResponse> => {
    const response = await axios.get(
      `${endPoints.dashboard.statistics.invoicing}`,
    );
    return response.data;
  };

export const getStatisticsOnHoldApi =
  async (): Promise<GetStatisticsOnHoldResponse> => {
    const response = await axios.get(
      `${endPoints.dashboard.statistics.onHold}`,
    );
    return response.data;
  };

export const getStatisticsPaymentsApi = async (
  params?: { paymentModels?: string[] },
): Promise<GetStatisticsPaymentsResponse> => {
  const response = await axios.get(
    `${endPoints.dashboard.statistics.payments}`,
    { params },
  );
  return response.data;
};

export const generatePaymentFileApi = async (
  companyId: string,
  params: GeneratePaymentFileParams,
  queryParams: { isBureau: boolean, isIreland: boolean }
): Promise<GeneratePaymentFileResponse> => {
  const { isBureau, isIreland } = queryParams;
  const response = await axios.put(
    `${endPoints.dashboard.generatePaymentFile}/${companyId}`,
    params,
    { params: isBureau ? { isIreland } : undefined }
  );
  return response.data;
};

export const confirmPaymentApi = async (
  params: ConfirmPaymentParams,
): Promise<ConfirmPaymentResponse> => {
  const response = await axios.put(
    `${endPoints.dashboard.confirmPayment}`,
    params,
  );
  return response.data;
};

export const getStatisticsPensionContributionApi = async (
  params: GetStatisticsPensionContributionParams = {},
): Promise<GetStatisticsPensionContributionResponse> => {
  const response = await axios.get(
    `${endPoints.dashboard.statistics.pensions}`,
    { params },
  );
  return response.data;
};

export const getStatisticsPensionSubcontractorsApi =
  async (): Promise<GetStatisticsPensionSubcontractorsResponse> => {
    const response = await axios.get(
      `${endPoints.dashboard.statistics.pensionSubcontractors}`,
    );
    return response.data;
  };

export const getStatisticsComplianceApi = async (
  params: GetStatisticsComplianceParams = {},
): Promise<GetStatisticsComplianceResponse> => {
  const response = await axios.get(
    `${endPoints.dashboard.statistics.compliance}`,
    { params },
  );
  return response.data;
};

export const getSafeRecErrorListApi = async (
  params: ComplianceErrorListParams = {},
): Promise<SafeRecErrorListResponse> => {
  const response = await axios.get(`${endPoints.payroll.safeRecErrors}`, {
    params,
  });
  return response.data;
};

export const getVeriPayeErrorListApi = async (
  params: ComplianceErrorListParams = {},
): Promise<VeriPayeErrorListResponse> => {
  const response = await axios.get(`${endPoints.veriPaye.errors}`, { params });
  return response.data;
};

export const resendSafeRecApi = async (
  params: SafeRecResendParams,
): Promise<{ success: boolean }> => {
  const response = await axios.post(`${endPoints.payroll.safeRec}`, params);
  return response.data;
};

export const resendVeriPayeApi = async (
  params: VeriPayeResendParams,
): Promise<{ success: boolean }> => {
  const response = await axios.post(
    `${endPoints.veriPaye.manuallySubmit}`,
    params,
  );
  return response.data;
};

export const getStatisticsEmployeeSubcontractorApi =
  async (): Promise<GetStatisticsEmployeeSubcontractorResponse> => {
    const response = await axios.get(
      `${endPoints.dashboard.statistics.subcontractorsAssignments}`,
    );
    return response.data;
  };

export const getStatisticsMiscellaneousApi =
  async (): Promise<GetStatisticsMiscellaneousResponse> => {
    const response = await axios.get(
      `${endPoints.dashboard.statistics.miscellaneous}`,
    );
    return response.data;
  };

export const getCompanyPaymentGroupingApi = async (
  params: GetCompanyPaymentGroupingParams,
): Promise<GetCompanyPaymentGroupingResponse> => {
  const response = await axios.get(
    `${endPoints.dashboard.companyPaymentGrouping}`,
    { params },
  );
  return response.data;
};

export const getStatisticsPayrollImportApi = async (
  params?: { paymentModels?: string[] },
): Promise<GetStatisticsPayrollImportResponse> => {
  const response = await axios.get(
    `${endPoints.dashboard.statistics.payrollImports}`,
    { params },
  );
  return response.data;
};

export const getStatisticsPayrollImportIrelandApi = async (
  params?: { paymentModels?: string[] },
): Promise<GetStatisticsPayrollImportResponse> => {
  const response = await axios.get(
    `${endPoints.dashboard.statistics.payrollImportsIreland}`,
    { params },
  );
  return response.data;
};

export const getStatisticsClientApprovalRejectApi = async (
  params?: { paymentModels?: string[] },
): Promise<GetStatisticsClientApprovalRejectResponse> => {
  const response = await axios.get(
    `${endPoints.dashboard.statistics.clientApprovalRejection}`,
    { params },
  );
  return response.data;
};

export const getApproverListApi = async (
  payrollId: string,
  params: GetListApproverParams,
): Promise<GetListApproverResponse> => {
  const response = await axios.get(
    `${endPoints.employee.getListApprover(payrollId)}`,
    { params },
  );
  return response.data;
};

export const requestApproverApi = async (
  payrollId: string,
  params: RequestApproverParams,
): Promise<RequestApproverResponse> => {
  const response = await axios.post(
    `${endPoints.payroll.requestApprover(payrollId)}`,
    params,
  );
  return response.data;
};

export const approvePayrollApi = async (
  params: ApprovePayrollParams,
): Promise<ApprovePayrollResponse> => {
  const response = await axios.post(
    `${endPoints.payroll.draft}/approve`,
    params,
  );
  return response.data;
};

export const rejectPayrollApi = async (
  params: RejectPayrollParams,
): Promise<RejectPayrollResponse> => {
  const response = await axios.post(
    `${endPoints.payroll.draft}/reject`,
    params,
  );
  return response.data;
};

export const getStatisticsRTISubmissionApi =
  async (): Promise<GetStatisticsRTISubmissionResponse> => {
    const response = await axios.get(
      `${endPoints.dashboard.statistics.rtiSubmissions}`,
    );
    return response.data;
  };

export const getStatisticsOthersApi =
  async (): Promise<GetStatisticsOthersResponse> => {
    const response = await axios.get(
      `${endPoints.dashboard.statistics.others}`,
      { params: { isBureau: true } },
    );
    return response.data;
  };

export const getStatisticsCommunicationApi =
  async (): Promise<GetStatisticsCommunicationResponse> => {
    const response = await axios.get(
      `${endPoints.dashboard.statistics.communication}`,
    );
    return response.data;
  };

export const getGroupingPayrollByPeriodApi =
  async (): Promise<GetGroupingPayrollByPeriodResponse> => {
    const response = await axios.get(
      `${endPoints.dashboard.payrollGroupingByPeriodAndFrequency}`,
    );
    return response.data;
  };

export const getStatisticsRevenueSubmissionApi =
  async (): Promise<GetStatisticsRevenueSubmissionResponse> => {
    const response = await axios.get(
      `${endPoints.dashboard.statistics.submitPSR}`,
    );
    return response.data;
  };

export const getStatisticsEmployeeIrelandApi =
  async (): Promise<GetStatisticsEmployeeIrelandResponse> => {
    const response = await axios.get(
      `${endPoints.dashboard.statistics.employees}`,
    );
    return response.data;
  };

export const uploadAgencyFileApi = async (
  agencyId: string,
  params: UploadAgencyFileParams,
): Promise<UploadAgencyFileResponse> => {
  const response = await axios.post(
    `${endPoints.import.agencyUpload}`,
    params,
    {
      params: {
        agencyRefs: [agencyId],
        agencyIds: [agencyId],
      },
    },
  );
  return response.data;
};

export const getAgencyUploadedFileListApi = async (
  params: GetAgencyUploadedFileListParams,
): Promise<GetAgencyUploadedFileListResponse> => {
  const response = await axios.get(`${endPoints.import.agencyUpload}`, {
    params,
  });
  return response.data;
};

export const getAgencyStatisticsApi = async (
  params: GetAgencyStatisticsParams,
): Promise<GetAgencyStatisticsResponse> => {
  const response = await axios.get(`${endPoints.dashboard.agency}`, { params });
  return response.data;
};

export const getStatisticsEmployeeDashboardApi = async (
  params: GetStatisticsEmployeeDashboardParams,
): Promise<GetStatisticsEmployeeDashboardResponse> => {
  const response = await axios.get(
    `${endPoints.dashboard.subcontractorPortal.general}`,
    { params },
  );
  return response.data;
};

export const getUpcomingPaymentApi = async (
  subcontractorId: string,
): Promise<UpcomingPaymentResponse> => {
  const response = await axios.get(
    endPoints.employee.upcomingPayment(subcontractorId),
  );
  return response.data;
};

export const getPayBreakdownApi = async (
  params?: PayBreakdownParams,
): Promise<PayBreakdownResponse> => {
  const response = await axios.get(endPoints.employee.payBreakdown, {
    params,
  });
  return response.data;
};

export const getTimesheetBreakdownApi =
  async (): Promise<TimesheetBreakdownResponse> => {
    const response = await axios.get(endPoints.employee.timesheetBreakdown);
    return response.data;
  };

export const getPayrollPeriodsApi =
  async (): Promise<PayrollPeriodsResponse> => {
    const response = await axios.get(
      endPoints.employee.allPayrollsTaxPeriodTaxYear,
    );
    return response.data;
  };

export const getPensionContributionApi = async (
  subcontractorId: string,
): Promise<GetPensionContributionResponse> => {
  const response = await axios.get(
    endPoints.dashboard.subcontractorPortal.pensionContribution(subcontractorId),
  );
  return response.data;
};

export const getDashboardRequiredAlertsList = async (
  params: GetDashboardRequiredAlertsListParams,
): Promise<GetDashboardRequiredAlertsListResponse> => {
  const response = await axios.get(endPoints.dashboard.requiredAlertsList, {
    params,
  });
  return response.data;
};
